import { campagneArchivee, compterCampagnes, etatPilotageCampagne } from './boutiqueClubPilotage';

const normaliser = (valeur) => String(valeur || '').trim().toLocaleLowerCase('fr-FR');
const normaliserCommercial = (valeur) => normaliser(valeur) || 'non attribué';
const normaliserNom = (valeur) => normaliser(valeur).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
export const DATE_PIVOT_FINANCIERE = /^\d{4}-\d{2}-\d{2}$/.test(process.env.REACT_APP_INTERCLUB_FINANCIAL_PIVOT || '')
  ? process.env.REACT_APP_INTERCLUB_FINANCIAL_PIVOT
  : '2025-04-01';
const montantDocument = (document) => {
  const donnees = document?.donnees || {};
  const valeur = donnees.montant_ttc ?? donnees.montant_commande ?? donnees.montant;
  if (valeur == null || valeur === '') return null;
  const montant = Number(valeur);
  return Number.isFinite(montant) ? montant : null;
};

export function syntheseFinanciere(situations = [], debut = '', fin = '') {
  const dansPeriode = (document) => {
    if (!debut && !fin) return true;
    const donnees = document?.donnees || {};
    const date = String(donnees.date_facturation || donnees.date_confirmation || donnees.date_creation || '').slice(0, 10);
    return Boolean(date) && (!debut || date >= debut) && (!fin || date <= fin);
  };
  const uniques = (documents) => {
    const ids = new Set();
    return documents.filter((document) => {
      if (document?.id == null) return true;
      const id = String(document.id);
      if (ids.has(id)) return false;
      ids.add(id);
      return true;
    });
  };
  const livraisons = uniques(situations.flatMap((situation) => situation.commandesAFacturer || [])).filter(dansPeriode);
  const impayees = uniques(situations.flatMap((situation) => situation.facturesOuvertes || [])).filter(dansPeriode);
  const somme = (liste) => liste.reduce((total, document) => total + (montantDocument(document) || 0), 0);
  const montantDisponible = (liste) => liste.every((document) => montantDocument(document) != null);
  return {
    livraisons: { nombre: livraisons.length, montant: montantDisponible(livraisons) ? somme(livraisons) : null },
    impayees: { nombre: impayees.length, montant: montantDisponible(impayees) ? somme(impayees) : null }
  };
}

const dateIsoValide = (valeur) => {
  const texte = String(valeur || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texte)) return '';
  const date = new Date(`${texte}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === texte ? texte : '';
};

export function syntheseAncienneteDettes(factures = [], clubs = [], situations = [], base = 'echeance', debut = '', fin = '', aujourdHui = new Date()) {
  // Une dette est un stock au jour D2. D1 (`debut`) est volontairement ignorée.
  const reference = dateIsoValide(fin) || dateIsoValide(aujourdHui instanceof Date ? aujourdHui.toISOString() : aujourdHui);
  const tranches = ['0–30 jours', '31–60 jours', '61–90 jours', '+90 jours'].map((label) => ({ label, clients: new Set(), factures: 0, centimes: 0, lignes: [] }));
  const clubsVisibles = new Set(clubs.map((club) => String(club.id || club.identifiant)));
  const echeances = new Map();
  clubs.forEach((club, index) => (situations[index]?.facturesOuvertes || []).forEach((document) => {
    const donnees = document.donnees || {};
    const numero = String(donnees.numero_facture || '').trim().toUpperCase();
    const date = dateIsoValide(donnees.date_echeance);
    if (numero && date) echeances.set(`${club.id || club.identifiant}|${numero}`, date);
  }));
  const vus = new Set();
  let sansDonnees = 0;
  let fallback = 0;
  const nonEchue = { label: 'NON ÉCHUE', clients: new Set(), factures: 0, centimes: 0, lignes: [] };
  const indisponible = { label: 'DATE INDISPONIBLE', clients: new Set(), factures: 0, centimes: 0, lignes: [] };
  const total = { clients: new Set(), factures: 0, centimes: 0 };
  factures.forEach((facture) => {
    const clubId = facture.club_id == null ? '' : String(facture.club_id);
    const identiteClient = clubId || String(facture.code_client || facture.nom_client || 'NON_RAPPROCHE');
    if ((clubId && !clubsVisibles.has(clubId)) || vus.has(String(facture.id))) return;
    vus.add(String(facture.id));
    const dateFacture = dateIsoValide(facture.date_facture);
    // Une dette est un stock à la date d'arrêté : le début de la période d'analyse
    // ne doit pas faire disparaître une facture antérieure encore ouverte.
    if (fin && dateFacture && dateFacture > fin) return;
    const montant = facture.reste_a_payer;
    if (montant == null || montant === '' || !Number.isFinite(Number(montant))) { sansDonnees += 1; return; }
    const centimes = Math.round(Number(montant) * 100);
    if (centimes <= 0) return;
    total.clients.add(identiteClient);
    total.factures += 1;
    total.centimes += centimes;
    const numero = String(facture.numero_facture || '').trim().toUpperCase();
    const echeance = dateIsoValide(facture.date_echeance) || echeances.get(`${clubId}|${numero}`) || '';
    const date = base === 'echeance' ? (echeance || dateFacture) : dateFacture;
    if (base === 'echeance' && !echeance && dateFacture) fallback += 1;
    if (!date) {
      indisponible.clients.add(identiteClient);
      indisponible.factures += 1;
      indisponible.centimes += centimes;
      indisponible.lignes.push({ ...facture, date_reference_utilisee: '', date_arrete: reference, ancienneteJours: null, tranche: indisponible.label });
      return;
    }
    const jours = Math.floor((new Date(`${reference}T12:00:00Z`) - new Date(`${date}T12:00:00Z`)) / 86400000);
    if (jours < 0) {
      nonEchue.clients.add(identiteClient);
      nonEchue.factures += 1;
      nonEchue.centimes += centimes;
      nonEchue.lignes.push({ ...facture, date_reference_utilisee: date, date_arrete: reference, ancienneteJours: jours, tranche: nonEchue.label });
      return;
    }
    const index = jours <= 30 ? 0 : jours <= 60 ? 1 : jours <= 90 ? 2 : 3;
    const tranche = tranches[index];
    tranche.clients.add(identiteClient);
    tranche.factures += 1;
    tranche.centimes += centimes;
    tranche.lignes.push({ ...facture, date_reference_utilisee: date, date_arrete: reference, ancienneteJours: jours, tranche: tranche.label });
  });
  const detail = tranches.map((tranche) => ({ label: tranche.label, clients: tranche.clients.size, factures: tranche.factures, montant: tranche.centimes / 100, clubIds: [...tranche.clients], lignes: tranche.lignes }));
  const dateIndisponible = { label: indisponible.label, clients: indisponible.clients.size, factures: indisponible.factures, montant: indisponible.centimes / 100, clubIds: [...indisponible.clients], lignes: indisponible.lignes };
  const detteNonEchue = { label: nonEchue.label, clients: nonEchue.clients.size, factures: nonEchue.factures, montant: nonEchue.centimes / 100, clubIds: [...nonEchue.clients], lignes: nonEchue.lignes };
  return {
    tranches: [...detail, {
      label: 'TOTAL',
      clients: total.clients.size,
      factures: total.factures,
      montant: total.centimes / 100,
      clubIds: [...total.clients]
    }],
    nonEchue: detteNonEchue,
    dateIndisponible,
    sansDonnees,
    fallback,
    dateArrete: reference
  };
}

// Le cache dette n'est exploitable que s'il couvre tous les comptes Interclub
// du périmètre. Une couverture partielle ne doit jamais être présentée comme 0 €.
export function sourceDetteInterclubComplete(situations = [], clubs = []) {
  if (!Array.isArray(situations) || !Array.isArray(clubs)) return false;
  const comptesAttendus = clubs.map((club) => ({
    id: String(club?.id || club?.identifiant || ''),
    code: String(club?.numero_compte_interclub || '').trim().toUpperCase()
  })).filter((club) => club.id && club.code);
  if (!comptesAttendus.length) return false;
  const ids = new Set(situations.map((situation) => String(situation?.club_id || '')).filter(Boolean));
  const codes = new Set(situations.map((situation) => String(situation?.code_client || '').trim().toUpperCase()).filter(Boolean));
  return comptesAttendus.every((club) => ids.has(club.id) || codes.has(club.code));
}

const dateDocument = (document) => {
  const donnees = document?.donnees || {};
  return String(donnees.date_facturation || donnees.date_paiement || donnees.date_confirmation || donnees.date_creation || '').slice(0, 10);
};

const dateMouvement = (document) => dateIsoValide(
  document?.donnees?.date_paiement
  || document?.donnees?.date_reglement
  || document?.donnees?.date_imputation
  || document?.donnees?.date_facturation
  || document?.donnees?.date_creation
);

const numeroCommande = (document) => String(document?.donnees?.numero_commande || (document?.type_document === 'COMMANDE' ? document?.identifiant_source : '') || '').trim();
const numeroFacture = (document) => String(document?.donnees?.numero_facture || document?.donnees?.numero_piece || (['FACTURE', 'AVOIR'].includes(document?.type_document) ? document?.identifiant_source : '') || '').trim().toUpperCase();
const codeClient = (document) => String(document?.donnees?.code_client || '').trim().toUpperCase();
const cleFacture = (document) => `${codeClient(document)}|${numeroFacture(document)}`;
const cleCommande = (document) => `${codeClient(document)}|${numeroCommande(document)}`;

const montantMouvement = (document) => Number(
  document?.donnees?.montant_impute
  ?? document?.donnees?.montant_ttc
  ?? document?.donnees?.montant_commande
  ?? document?.donnees?.montant
  ?? 0
);

const dateMetierDocument = (document) => {
  const donnees = document?.donnees || {};
  if (document?.type_document === 'DEVIS') return dateIsoValide(donnees.date_creation || donnees.date_devis);
  if (document?.type_document === 'COMMANDE') return dateIsoValide(donnees.date_confirmation || donnees.date_creation);
  if (['FACTURE', 'AVOIR'].includes(document?.type_document)) return dateIsoValide(donnees.date_facturation || donnees.date_creation);
  if (document?.type_document === 'PAIEMENT') return dateMouvement(document);
  if (['BL', 'BON_LIVRAISON'].includes(document?.type_document)) return dateIsoValide(donnees.date_bl || donnees.date_livraison || donnees.date_creation);
  return dateIsoValide(dateDocument(document));
};

// Clé métier commune au cache global et à la situation client directe. Pour un
// règlement, les deux sources peuvent porter des identifiants techniques
// différents : facture + date effective + montant + client (+ commande) est
// alors la signature stable disponible dans les deux flux.
export function cleMetierDocumentInterclub(document) {
  const type = String(document?.type_document || '').trim().toUpperCase();
  const donnees = document?.donnees || {};
  const code = codeClient(document);
  if (type === 'PAIEMENT') {
    const facture = numeroFacture(document);
    const date = dateMouvement(document);
    const montant = Math.round(montantMouvement(document) * 100);
    return [type, code, facture, date, montant].join('|');
  }
  if (type === 'DEVIS') return [type, code, String(donnees.numero_devis || document?.identifiant_source || document?.id || '').trim().toUpperCase()].join('|');
  if (type === 'COMMANDE') return [type, code, numeroCommande(document)].join('|');
  if (['FACTURE', 'AVOIR'].includes(type)) return [type, code, numeroFacture(document)].join('|');
  if (['BL', 'BON_LIVRAISON'].includes(type)) return ['BL', code, String(donnees.numero_bl || document?.identifiant_source || document?.id || '').trim().toUpperCase()].join('|');
  return [type, code, String(document?.identifiant_source || document?.id || '').trim().toUpperCase()].join('|');
}

const richesseDocument = (document) => {
  const donnees = document?.donnees || {};
  return ['identifiant_reglement', 'montant_impute', 'reste_a_payer', 'numero_commande', 'commercial_commande', 'statut']
    .reduce((score, cle) => score + (donnees[cle] !== null && donnees[cle] !== undefined && donnees[cle] !== '' ? 1 : 0), 0);
};

const horodatageDocument = (document) => String(
  document?.derniere_observation
  || document?.synchronise_at
  || document?.updated_at
  || document?.created_at
  || ''
);

export function documentsInterclubMetierUniques(documents = []) {
  const uniques = new Map();
  (documents || []).forEach((document) => {
    const cle = cleMetierDocumentInterclub(document);
    const precedent = uniques.get(cle);
    const plusRiche = richesseDocument(document) > richesseDocument(precedent);
    const aussiRicheEtPlusRecent = richesseDocument(document) === richesseDocument(precedent)
      && horodatageDocument(document) >= horodatageDocument(precedent);
    // Une même commande est observée plusieurs fois au fil des synchronisations.
    // À richesse égale, conserver le dernier état métier évite de ressusciter un
    // ancien snapshot « Commande confirmée » après sa facturation/régularisation.
    if (!precedent || plusRiche || aussiRicheEtPlusRecent) uniques.set(cle, document);
  });
  return [...uniques.values()];
}
const idDocument = (document) => String(document?.identifiant_source || document?.id || '');
const documentsUniques = (documents) => [...new Map(documents.map((document) => [idDocument(document), document])).values()];
const dateFacture = (document) => dateIsoValide(document?.donnees?.date_facturation || document?.donnees?.date_facture || document?.donnees?.date_creation);

export function rapprocherClientsInterclub(documents = [], clubs = []) {
  const clubsParCompte = new Map(clubs.map((club) => [String(club.numero_compte_interclub || '').trim().toUpperCase(), club]).filter(([code]) => code));
  const codes = new Map();
  documents.forEach((document) => {
    const code = codeClient(document);
    if (!code) return;
    const donnees = document.donnees || {};
    const entree = codes.get(code) || { codeClient: code, nomClient: '', documents: [] };
    entree.nomClient = entree.nomClient || donnees.nom_client || donnees.client || '';
    entree.documents.push(document);
    codes.set(code, entree);
  });
  return [...codes.values()].map((entree) => {
    const exact = clubsParCompte.get(entree.codeClient);
    if (exact) return { ...entree, statut: 'RAPPROCHÉ', club: exact, candidats: [], raison: 'Même numéro de compte Interclub.' };
    const nom = normaliserNom(entree.nomClient);
    const candidats = nom ? clubs.map((club) => {
      const nomClub = normaliserNom(club.nom);
      const mots = new Set(nom.split(' ').filter((mot) => mot.length > 2));
      const communs = [...mots].filter((mot) => nomClub.includes(mot)).length;
      const score = nomClub === nom ? 100 : (mots.size ? Math.round(communs / mots.size * 100) : 0);
      return { club, score };
    }).filter((item) => item.score >= 50).sort((a, b) => b.score - a.score).slice(0, 3) : [];
    return candidats.length
      ? { ...entree, statut: 'À RAPPROCHER', club: null, candidats, raison: 'Nom proche uniquement : validation humaine obligatoire.' }
      : { ...entree, statut: 'NON RAPPROCHÉ', club: null, candidats: [], raison: 'Aucun numéro de compte ni candidat de nom fiable.' };
  });
}

const valeurNumerique = (donnees, cles) => {
  for (const cle of cles) {
    if (donnees?.[cle] == null || donnees?.[cle] === '') continue;
    const valeur = Number(donnees[cle]);
    if (Number.isFinite(valeur)) return valeur;
  }
  return null;
};

const cleCommandeDocument = (document) => `${codeClient(document)}|${numeroCommande(document)}`;

const commandesEtatCourant = (documents = []) => {
  const map = new Map();
  documents.filter((document) => document?.type_document === 'COMMANDE').forEach((document) => {
    const cle = cleCommandeDocument(document);
    if (!cle || cle.endsWith('|')) return;
    const precedent = map.get(cle);
    const horodatage = String(document.synchronise_at || document.updated_at || document.created_at || '');
    const horodatagePrecedent = String(precedent?.synchronise_at || precedent?.updated_at || precedent?.created_at || '');
    if (!precedent || horodatage >= horodatagePrecedent) map.set(cle, document);
  });
  return [...map.values()];
};

const trancheAnciennete = (jours) => jours <= 30 ? '0–30 jours' : jours <= 60 ? '31–60 jours' : jours <= 90 ? '61–90 jours' : '+90 jours';

export function syntheseCommandesConfirmeesAFacturer(documents = [], clubs = [], options = {}) {
  const reference = dateIsoValide(options.dateArrete || options.fin || new Date().toISOString()) || new Date().toISOString().slice(0, 10);
  const commercialCible = normaliser(options.commercial);
  const tousCommerciaux = !commercialCible || ['tous', "toute l'équipe"].includes(commercialCible);
  const clubParCode = new Map(clubs.map((club) => [String(club.numero_compte_interclub || club.code || '').trim().toUpperCase(), club]));
  const tranches = ['0–30 jours', '31–60 jours', '61–90 jours', '+90 jours'].map((label) => ({ label, commandes: 0, centimes: 0, clubIds: new Set(), lignes: [] }));
  const indisponible = { label: 'DATE INDISPONIBLE', commandes: 0, centimes: 0, clubIds: new Set(), lignes: [] };
  const ambigues = [];
  const nonRapproches = [];
  const lignes = [];

  const documentsMetier = documentsInterclubMetierUniques(documents);
  const factureParCommande = new Map();
  const facturesParCommande = new Map();
  documentsMetier.filter((document) => ['FACTURE', 'AVOIR'].includes(document?.type_document)).forEach((document) => {
    const cle = cleCommandeDocument(document);
    const date = dateFacture(document);
    if (!cle || cle.endsWith('|')) return;
    const liste = facturesParCommande.get(cle) || [];
    liste.push({ document, date });
    facturesParCommande.set(cle, liste);
    if (date && date > reference) return;
    // Une facture consomme le montant restant à facturer. Un avoir est une
    // pièce financière distincte : il ne doit jamais « ressusciter » une
    // commande déjà facturée dans le stock des commandes à facturer.
    if (document.type_document === 'FACTURE') {
      factureParCommande.set(cle, (factureParCommande.get(cle) || 0) + Math.abs(montantMouvement(document)));
    }
  });

  commandesEtatCourant(documentsMetier).forEach((document) => {
    const donnees = document.donnees || {};
    const statut = normaliser(donnees.statut || donnees.etat || document.statut);
    // Pour une situation historique, l'état courant « totalement facturée » ne
    // doit pas effacer une commande qui était encore à facturer à la date
    // d'arrêté. La confirmation et les factures datées reconstruisent le stock.
    const estConfirmee = statut.includes('confirm') || statut.includes('factur') || statut.includes('régl') || statut.includes('regl') || statut.includes('sold');
    if (!estConfirmee) {
      if (statut.includes('confirm') && !statut.includes('totalement')) ambigues.push(document);
      return;
    }
    const commercial = normaliserCommercial(donnees.commercial_commande || donnees.commercial);
    if (!tousCommerciaux && commercial !== commercialCible) return;
    const commande = valeurNumerique(donnees, ['montant_commande', 'montant_ttc', 'montant']);
    const cleCommandeCourante = cleCommandeDocument(document);
    const factureHistorique = Math.max(0, factureParCommande.get(cleCommandeCourante) || 0);
    const piecesCommande = facturesParCommande.get(cleCommandeCourante) || [];
    const facturesDatees = piecesCommande.filter((item) => item.document.type_document === 'FACTURE' && item.date);
    const factureApresD2 = facturesDatees.some((item) => item.date > reference);
    const factureAvantOuAD2 = facturesDatees.some((item) => item.date <= reference);
    const statutConfirmeCourant = statut === 'commande confirmée' || statut === 'commande confirmee';
    if (commande == null) { ambigues.push(document); return; }
    const code = codeClient(document);
    const club = clubParCode.get(code);
    const clubId = String(club?.id || club?.identifiant || '');
    const date = dateIsoValide(donnees.date_confirmation);
    // Un reste à facturer est un stock à la date d'arrêté. Une commande ancienne
    // encore confirmée reste donc incluse ; seule une confirmation postérieure à
    // la situation demandée est exclue. Une date absente reste contrôlable dans
    // DATE INDISPONIBLE au lieu d'être silencieusement perdue.
    if (date && date > reference) return;
    if (!date) { ambigues.push({ ...document, categorieAudit: 'F', raisonAudit: 'DATE_CONFIRMATION_ABSENTE' }); return; }

    const montantFactureCourant = valeurNumerique(donnees, ['montant_facture', 'montant_deja_facture', 'cumul_facture']) ?? 0;
    // Un état courant déjà facturé/réglé/soldé n'est reconstructible à D2 que
    // par des factures datées. Sans cet événement, la ligne reste en audit F et
    // ne pollue pas le KPI. Une facture postérieure à D2 laisse au contraire la
    // commande dans le stock historique demandé.
    if (!statutConfirmeCourant && !facturesDatees.length) {
      ambigues.push({ ...document, categorieAudit: 'F', raisonAudit: 'HISTORIQUE_FACTURATION_ABSENT' });
      return;
    }
    if (statutConfirmeCourant && montantFactureCourant > 0 && !factureAvantOuAD2 && !factureApresD2) {
      ambigues.push({ ...document, categorieAudit: 'F', raisonAudit: 'MONTANT_FACTURE_SANS_EVENEMENT_DATE' });
      return;
    }
    if (!statutConfirmeCourant && factureAvantOuAD2 && !factureApresD2 && factureHistorique + 0.009 < commande) {
      ambigues.push({ ...document, categorieAudit: 'F', raisonAudit: 'STATUT_FACTURE_CONTRADICTOIRE_AVEC_MONTANT_DATE' });
      return;
    }
    const montant = Math.max(0, commande - factureHistorique);
    if (montant <= 0.009) return;
    const statutAD2 = factureHistorique > 0
      ? 'Commande confirmée — partiellement facturée à D2'
      : 'Commande confirmée — non facturée à D2';
    const ligne = {
      id: idDocument(document), numeroCommande: numeroCommande(document), codeClient: code, clubCode: club?.code || '',
      client: club?.nom || donnees.nom_client || code || '—', clubId, commercial: donnees.commercial_commande || donnees.commercial || '',
      dateCommande: dateIsoValide(donnees.date_creation), dateConfirmation: date,
      ancienneteJours: date ? Math.max(0, Math.floor((new Date(`${reference}T12:00:00Z`) - new Date(`${date}T12:00:00Z`)) / 86400000)) : null,
      statut: statutAD2, statutCourant: donnees.statut || donnees.etat || document.statut || '', montantCommande: commande, montant
    };
    if (!clubId) nonRapproches.push(ligne);
    lignes.push(ligne);
    const cible = date ? tranches.find((item) => item.label === trancheAnciennete(ligne.ancienneteJours)) : indisponible;
    ligne.tranche = cible.label;
    cible.commandes += 1;
    cible.centimes += Math.round(montant * 100);
    if (clubId) cible.clubIds.add(clubId);
    cible.lignes.push(ligne);
  });
  const formater = (item) => ({ ...item, montant: item.centimes / 100, clubIds: [...item.clubIds] });
  const detail = tranches.map(formater);
  const dateIndisponible = formater(indisponible);
  const total = {
    label: 'TOTAL', commandes: lignes.length,
    montant: lignes.reduce((somme, ligne) => somme + ligne.montant, 0),
    clubIds: [...new Set(lignes.map((ligne) => ligne.clubId).filter(Boolean))], lignes
  };
  return {
    tranches: detail, dateIndisponible, total: { ...total, montant: Math.round(total.montant * 100) / 100 },
    lignes, ambigues, nonRapproches, reference,
    certifiable: true,
    dossierComplet: ambigues.length === 0,
    audit: { A: { commandes: lignes.length, montant: Math.round(total.montant * 100) / 100, lignes }, F: { commandes: ambigues.length, lignes: ambigues } }
  };
}

export function syntheseTransformationDevis(documents = [], clubs = [], options = {}) {
  const commercialCible = normaliser(options.commercial);
  const tousCommerciaux = !commercialCible || ['tous', "toute l'équipe"].includes(commercialCible);
  const clubsParCode = new Map(clubs.map((club) => [String(club.numero_compte_interclub || '').trim().toUpperCase(), club]));
  const commandes = new Map(documents
    .filter((document) => document?.type_document === 'COMMANDE')
    .map((document) => [numeroCommande(document), document])
    .filter(([numero]) => numero));
  const devis = documentsUniques(documents.filter((document) => document?.type_document === 'DEVIS')).filter((document) => {
    if (!clubsParCode.has(codeClient(document))) return false;
    const date = dateDocument(document);
    if ((options.debut || options.fin) && !date) return false;
    if (options.debut && date < options.debut) return false;
    if (options.fin && date > options.fin) return false;
    const commande = commandes.get(numeroCommande(document));
    const commercial = normaliser(commande?.donnees?.commercial_commande || document?.donnees?.commercial_commande || document?.donnees?.commercial);
    // L'export global des devis ne porte pas toujours le commercial. Dans ce cas,
    // le périmètre est déjà borné par la liste de clubs fournie par l'appelant.
    return tousCommerciaux || !commercial || commercial === commercialCible;
  });
  const transformes = devis.filter((document) => {
    const statut = normaliser(document?.donnees?.statut || document?.donnees?.etat || document?.statut);
    const numero = numeroCommande(document);
    return Boolean((numero && commandes.has(numero)) || statut.includes('transf'));
  });
  const clubIds = [...new Set(devis.map((document) => {
    const club = clubsParCode.get(codeClient(document));
    return String(club?.id || club?.identifiant || '');
  }).filter(Boolean))];
  const denominateur = devis.length;
  const numerateur = transformes.length;
  return { devis: denominateur, transformes: numerateur, numerateur, denominateur,
    taux: denominateur ? numerateur / denominateur * 100 : 0,
    formule: "devis transformés en commande ÷ nombre total de devis × 100", clubIds };
}

function cycleFinancier(documents, options = {}) {
  const stock = options.stock === true;
  const pivot = stock ? '' : (dateIsoValide(options.pivot) || DATE_PIVOT_FINANCIERE);
  const facturesToutes = documentsUniques(documents.filter((document) => document?.type_document === 'FACTURE'));
  const factures = facturesToutes.filter((document) => {
    const date = dateFacture(document);
    if (!date) return false;
    if (stock) return !options.fin || date <= options.fin;
    return date >= pivot && (!options.debut || date >= options.debut) && (!options.fin || date <= options.fin);
  });
  const facturesParCle = new Map(factures.map((document) => [cleFacture(document), document]));
  const commandesIncluses = new Set(factures.map(cleCommande));
  const mouvements = documentsUniques(documents.filter((document) => ['PAIEMENT', 'AVOIR'].includes(document?.type_document))).filter((document) => {
    if (!stock || !options.fin) return true;
    const date = dateMouvement(document);
    return !date || date <= options.fin;
  });
  const rattache = (document) => facturesParCle.has(cleFacture(document)) || commandesIncluses.has(cleCommande(document));
  const paiements = mouvements.filter((document) => document.type_document === 'PAIEMENT' && facturesParCle.has(cleFacture(document)));
  const avoirs = mouvements.filter((document) => document.type_document === 'AVOIR' && rattache(document));
  return {
    pivot,
    factures,
    facturesToutes,
    facturesParCle,
    commandesIncluses,
    paiements,
    avoirs,
    facturesExclues: facturesToutes.filter((document) => !factures.includes(document)),
    paiementsExclus: mouvements.filter((document) => document.type_document === 'PAIEMENT' && !facturesParCle.has(cleFacture(document))),
    reglementsOrphelins: paiements.filter((document) => !facturesParCle.has(cleFacture(document))).length
  };
}

export function mouvementsClubCanoniques(documents = [], club = {}, options = {}) {
  const code = String(club.numero_compte_interclub || '').trim().toUpperCase();
  const documentsUniquesMetier = documentsInterclubMetierUniques(documents);
  const documentsClub = documentsUniquesMetier.filter((document) => codeClient(document) === code);
  const commandes = new Map(documentsUniquesMetier
    .filter((document) => document?.type_document === 'COMMANDE')
    .map((document) => [numeroCommande(document), document])
    .filter(([numero]) => numero));
  const commercialCible = normaliser(options.commercial);
  const tousCommerciaux = !commercialCible || ['tous', "toute l'équipe"].includes(commercialCible);
  const commercialDocument = (document) => normaliserCommercial(
    commandes.get(numeroCommande(document))?.donnees?.commercial_commande
    || document?.donnees?.commercial_commande
    || document?.donnees?.commercial
  );
  const dansCommercial = (document) => tousCommerciaux || commercialDocument(document) === commercialCible;
  const dansPeriode = (document, financier = false) => {
    const date = dateMetierDocument(document);
    const debut = options.debut || (financier ? (dateIsoValide(options.pivot) || DATE_PIVOT_FINANCIERE) : '');
    if ((debut || options.fin) && !date) return false;
    return (!debut || date >= debut) && (!options.fin || date <= options.fin);
  };
  const selection = (type, financier = false) => documentsClub.filter((document) =>
    document.type_document === type && dansCommercial(document) && dansPeriode(document, financier)
  );
  const devis = selection('DEVIS');
  const commandesPeriode = selection('COMMANDE');
  const factures = selection('FACTURE', true);
  const avoirs = selection('AVOIR', true);
  // Le paiement appartient à la période de sa date effective, indépendamment
  // de la date de la facture à laquelle il est imputé.
  const paiements = selection('PAIEMENT', true);
  const logistique = documentsClub.filter((document) => ['BL', 'BON_LIVRAISON'].includes(document.type_document) && dansCommercial(document) && dansPeriode(document));
  const aFacturer = syntheseCommandesConfirmeesAFacturer(documentsClub, [club], {
    ...options, commercial: options.commercial, dateArrete: options.dateArrete || options.fin
  });
  const somme = (items) => items.reduce((total, document) => total + montantMouvement(document), 0);
  return {
    devis,
    commandes: commandesPeriode,
    factures,
    avoirs,
    paiements,
    logistique,
    aFacturer,
    documents: [...devis, ...commandesPeriode, ...logistique, ...factures, ...avoirs, ...paiements],
    synthese: {
      devis: { nombre: devis.length, montant: somme(devis) },
      commandes: { nombre: commandesPeriode.length, montant: somme(commandesPeriode) },
      facture: somme(factures) - Math.abs(somme(avoirs)),
      paye: somme(paiements),
      resteDu: null,
      detteDisponible: false,
      aFacturer: { nombre: aFacturer.total.commandes, montant: aFacturer.total.montant, certifie: true, lignes: aFacturer.lignes }
    }
  };
}

export function synthesesClubsInterclub(documents = [], clubs = [], options = {}) {
  const resultats = new Map();
  clubs.forEach((club) => {
    const id = String(club.id || club.identifiant || '');
    resultats.set(id, mouvementsClubCanoniques(documents, club, options).synthese);
  });
  return resultats;
}

// Résolution unique document Interclub -> Club CRM. Une ambiguïté n'est jamais
// arbitrée par proximité de nom : elle reste visible et doit être contrôlée.
export function resoudreClubDocumentInterclub(document = {}, clubs = []) {
  const donnees = document?.donnees || {};
  const clubIdDocument = String(document?.club_id || donnees.club_id || '').trim();
  const code = codeClient(document);
  const parId = clubIdDocument
    ? clubs.filter((club) => String(club?.id || club?.identifiant || '') === clubIdDocument)
    : [];
  const parCode = code
    ? clubs.filter((club) => String(club?.numero_compte_interclub || '').trim().toUpperCase() === code)
    : [];
  const candidats = parId.length ? parId : parCode;
  if (candidats.length === 1) {
    const club = candidats[0];
    return {
      clubId: String(club.id || club.identifiant || ''),
      clubCode: String(club.code || ''),
      clubName: String(club.nom || ''),
      mappingStatus: 'RAPPROCHÉ'
    };
  }
  if (candidats.length > 1) return { clubId: null, clubCode: '', clubName: '', mappingStatus: 'AMBIGU' };
  return { clubId: null, clubCode: '', clubName: '', mappingStatus: 'NON RAPPROCHÉ' };
}

export function facturesCanoniquesInterclub(documents = [], clubs = [], options = {}) {
  const documentsMetier = documentsInterclubMetierUniques(documents);
  const commandes = new Map(documentsMetier.filter((document) => document?.type_document === 'COMMANDE').map((document) => [cleCommande(document), document]).filter(([cle]) => !cle.endsWith('|')));
  const commercialCible = normaliser(options.commercial);
  const tousCommerciaux = !commercialCible || ['tous', "toute l'équipe"].includes(commercialCible);
  // La dette est un stock à date : pas de borne basse/pivot, paiements et avoirs
  // retenus uniquement jusqu'à la date de situation.
  const cycle = cycleFinancier(documentsMetier, { ...options, debut: '', stock: true });
  const paiements = new Map();
  cycle.paiements.forEach((document) => {
    const cle = `${codeClient(document)}|${numeroFacture(document)}`;
    if (!paiements.has(cle)) paiements.set(cle, []);
    paiements.get(cle).push(document);
  });
  const creditsParCommande = new Map();
  const creditsParFacture = new Map();
  cycle.avoirs.forEach((document) => {
    const numero = numeroFacture(document);
    if (numero && cycle.facturesParCle.has(cleFacture(document))) {
      const cle = cleFacture(document);
      creditsParFacture.set(cle, (creditsParFacture.get(cle) || 0) + Math.abs(Number(document.donnees?.montant_ttc ?? document.donnees?.montant ?? 0)));
      return;
    }
    const cle = `${codeClient(document)}|${numeroCommande(document)}`;
    creditsParCommande.set(cle, (creditsParCommande.get(cle) || 0) + Math.abs(Number(document.donnees?.montant_ttc ?? document.donnees?.montant ?? 0)));
  });
  const lignes = cycle.factures.flatMap((document) => {
    const resolution = resoudreClubDocumentInterclub(document, clubs);
    const commercial = normaliserCommercial(commandes.get(cleCommande(document))?.donnees?.commercial_commande || document.donnees?.commercial_commande || document.donnees?.commercial);
    if (!tousCommerciaux && commercial !== commercialCible) return [];
    const date = dateFacture(document);
    const imputations = paiements.get(`${codeClient(document)}|${numeroFacture(document)}`) || [];
    const montantFacture = Number(document.donnees?.montant_ttc ?? document.donnees?.montant ?? 0);
    const montantPaye = imputations.reduce((total, paiement) => total + Number(paiement.donnees?.montant_impute ?? paiement.donnees?.montant ?? 0), 0);
    const restes = imputations.map((paiement) => Number(paiement.donnees?.reste_a_payer)).filter(Number.isFinite);
    return [{
      id: `interclub-${codeClient(document)}-${numeroFacture(document)}`,
      code_client: codeClient(document),
      nom_client: String(document.donnees?.nom_client || document.donnees?.client || ''),
      club_id: resolution.clubId,
      code_club: resolution.clubCode,
      nom_club: resolution.clubName,
      mappingStatus: resolution.mappingStatus,
      numero_facture: numeroFacture(document),
      date_facture: date,
      date_echeance: document.donnees?.date_echeance || '',
      numero_commande: numeroCommande(document),
      montant_facture: montantFacture,
      montant_paye: montantPaye,
      reste_a_payer: Math.max(0, restes.length ? Math.min(...restes) : montantFacture - montantPaye),
      commercial_commande: commercial
    }];
  });
  const creditsRestants = new Map(creditsParCommande);
  return lignes.sort((a, b) => String(a.date_facture).localeCompare(String(b.date_facture))).map((facture) => {
    const code = facture.code_client || '';
    const creditFacture = creditsParFacture.get(`${code}|${facture.numero_facture}`) || 0;
    const cle = `${code}|${facture.numero_commande}`;
    const credit = creditsRestants.get(cle) || 0;
    const deductionFacture = Math.min(creditFacture, facture.reste_a_payer);
    const apresFacture = facture.reste_a_payer - deductionFacture;
    const deductionCommande = Math.min(credit, apresFacture);
    creditsRestants.set(cle, credit - deductionCommande);
    return { ...facture, montant_avoirs: deductionFacture + deductionCommande, reste_a_payer: apresFacture - deductionCommande };
  });
}

export function syntheseDetteALaDate(documents = [], clubs = [], options = {}) {
  const dateArrete = dateIsoValide(options.dateArrete || options.fin || new Date().toISOString());
  const factures = facturesCanoniquesInterclub(documentsInterclubMetierUniques(documents), clubs, {
    commercial: options.commercial,
    fin: dateArrete
  });
  return syntheseAncienneteDettes(factures, clubs, [], 'echeance', '', dateArrete, dateArrete);
}

export function syntheseLivraisonsInterclub(documents = [], clubs = [], options = {}) {
  const documentsMetier = documentsInterclubMetierUniques(documents);
  const documentsBl = documentsMetier.filter((document) => ['BL', 'BON_LIVRAISON'].includes(document?.type_document));
  const commandes = new Map(documentsMetier.filter((document) => document?.type_document === 'COMMANDE')
    .map((document) => [cleCommandeDocument(document), document]).filter(([cle]) => !cle.endsWith('|')));
  const commercialCible = normaliser(options.commercial);
  const tousCommerciaux = !commercialCible || ['tous', "toute l'équipe"].includes(commercialCible);
  const candidats = documentsBl.map((document) => {
    const commande = commandes.get(cleCommandeDocument(document));
    const commercial = normaliserCommercial(commande?.donnees?.commercial_commande || document?.donnees?.commercial_commande || document?.donnees?.commercial);
    return { document, commande, commercial, date: dateMetierDocument(document) };
  }).filter(({ commercial }) => tousCommerciaux || commercial === commercialCible);
  const sansDate = (options.debut || options.fin) ? candidats.filter(({ date }) => !date).length : 0;
  const lignes = candidats.filter(({ date }) => {
    if ((options.debut || options.fin) && !date) return false;
    if (options.debut && date < options.debut) return false;
    if (options.fin && date > options.fin) return false;
    return true;
  }).map(({ document, commercial, date }) => {
    const resolution = resoudreClubDocumentInterclub(document, clubs);
    return {
      document,
      codeClient: codeClient(document),
      numeroCommande: numeroCommande(document),
      numeroLivraison: String(document?.donnees?.numero_bl || document?.identifiant_source || document?.id || ''),
      date,
      clubId: resolution.clubId,
      club: resolution.clubName || String(document?.donnees?.nom_client || document?.donnees?.client || codeClient(document) || 'Client non rapproché'),
      commercial,
      type: 'BON DE LIVRAISON',
      montant: montantMouvement(document),
      statut: String(document?.donnees?.statut || document?.statut || 'Non renseigné'),
      source: 'Interclub — BL',
      mappingStatus: resolution.mappingStatus
    };
  });
  const sourceDisponible = documentsBl.length > 0;
  const certifiable = sourceDisponible && sansDate === 0;
  return {
    certifiable,
    sourceDisponible,
    motifIndisponibilite: !sourceDisponible
      ? 'Aucun bon de livraison Interclub n’est synchronisé.'
      : sansDate > 0 ? `${sansDate} bon(s) de livraison sans date empêchent de certifier la période.` : '',
    nombre: certifiable ? lignes.length : null,
    montant: certifiable ? Math.round(lignes.reduce((total, ligne) => total + ligne.montant, 0) * 100) / 100 : null,
    lignes,
    sansDate
  };
}

const origineEcriture = (ecriture) => normaliser(ecriture?.origine);
const estEmissionFacture = (ecriture) => /^F/i.test(String(ecriture?.numero_piece || ''))
  && /facture/.test(origineEcriture(ecriture)) && !/(paiemt|paiement|reglement|règlement|imputation)/.test(origineEcriture(ecriture));
const estPaiementFacture = (ecriture) => /^F/i.test(String(ecriture?.numero_piece || ''))
  && /(paiemt|paiement|reglement|règlement|imputation)/.test(origineEcriture(ecriture));
const estAvoir = (ecriture) => /^A/i.test(String(ecriture?.numero_piece || '')) || /avoir/.test(origineEcriture(ecriture));

function documentsSituationsDette(situations = []) {
  return situations.flatMap((situation) => {
    const code = String(situation?.code_client || '').trim().toUpperCase();
    const documentsDirects = [...(Array.isArray(situation?.documents) ? situation.documents : []), ...(Array.isArray(situation?.paiements) ? situation.paiements : [])];
    const documentsEcritures = (Array.isArray(situation?.ecritures) ? situation.ecritures : []).flatMap((ecriture, index) => {
      const numeroPiece = String(ecriture?.numero_piece || '').trim().toUpperCase();
      const commun = {
        code_client: code,
        numero_commande: String(ecriture?.numero_commande || '').trim().toUpperCase(),
        numero_facture: numeroPiece,
        origine: ecriture?.origine || '',
        date_creation: dateIsoValide(ecriture?.date)
      };
      if (estEmissionFacture(ecriture) && Number(ecriture?.debit || 0) > 0) return [{
        type_document: 'FACTURE', identifiant_source: ecriture?.id_source || `situation-facture-${code}-${numeroPiece}-${index}`,
        donnees: { ...commun, date_facturation: commun.date_creation, date_echeance: dateIsoValide(ecriture?.date_echeance), montant_ttc: Number(ecriture.debit) }
      }];
      if (estPaiementFacture(ecriture) && Number(ecriture?.credit || 0) > 0) return [{
        type_document: 'PAIEMENT', identifiant_source: ecriture?.id_source || `situation-paiement-${code}-${numeroPiece}-${index}`,
        donnees: { ...commun, date_paiement: commun.date_creation, montant_impute: Number(ecriture.credit) }
      }];
      if (estAvoir(ecriture) && Math.max(Number(ecriture?.credit || 0), Number(ecriture?.debit || 0)) > 0) return [{
        type_document: 'AVOIR', identifiant_source: ecriture?.id_source || `situation-avoir-${code}-${numeroPiece}-${index}`,
        donnees: { ...commun, date_facturation: commun.date_creation, montant_ttc: Math.max(Number(ecriture?.credit || 0), Number(ecriture?.debit || 0)) }
      }];
      return [];
    });
    return [...documentsDirects, ...documentsEcritures];
  });
}

// Une dette historique n'est certifiée que si le cache couvre chaque compte et
// fournit un grand livre assez récent pour reconstruire facture - avoirs -
// règlements à D2. Aucun solde courant ne sert de repli.
export function certifierDetteDepuisSituationsInterclub(situations = [], documents = [], clubs = [], options = {}) {
  const dateArrete = dateIsoValide(options.fin || new Date().toISOString());
  const raisons = [];
  if (!Array.isArray(situations) || situations.length === 0) raisons.push('CACHE_SITUATIONS_ABSENT');
  const documentsCombines = documentsInterclubMetierUniques([...(documents || []), ...documentsSituationsDette(situations)]);
  const facturesToutes = dateArrete ? facturesCanoniquesInterclub(documentsCombines, clubs, { commercial: options.commercial, fin: dateArrete }) : [];
  const facturesPreliminaires = facturesToutes.filter((facture) => Number(facture.reste_a_payer || 0) > 0.009);
  const commercialCible = normaliser(options.commercial);
  const tousCommerciaux = !commercialCible || ['tous', "toute l'équipe"].includes(commercialCible);
  const commandes = documentsCombines.filter((document) => document?.type_document === 'COMMANDE').filter((document) => {
    const commercial = normaliserCommercial(document?.donnees?.commercial_commande || document?.donnees?.commercial);
    return tousCommerciaux || commercial === commercialCible;
  });
  const codesRequis = new Set([
    ...facturesToutes.map((facture) => String(facture.code_client || '').trim().toUpperCase()),
    ...commandes.map((document) => codeClient(document))
  ].filter(Boolean));
  // Sans aucun document global, une situation connue reste une preuve de
  // périmètre à contrôler, jamais une autorisation implicite d'afficher 0 €.
  if (!codesRequis.size) (situations || []).forEach((situation) => {
    const code = String(situation?.code_client || '').trim().toUpperCase();
    if (code) codesRequis.add(code);
  });
  const situationsParCode = new Map((situations || []).map((situation) => [String(situation?.code_client || '').trim().toUpperCase(), situation]).filter(([code]) => code));
  const manquants = [];
  codesRequis.forEach((code) => {
    const club = (clubs || []).find((item) => String(item?.numero_compte_interclub || '').trim().toUpperCase() === code);
    const situation = situationsParCode.get(code) || (situations || []).find((item) => String(item?.club_id || '') === String(club?.id || club?.identifiant || ''));
    if (!situation) { manquants.push(code); return; }
    if (!Array.isArray(situation.ecritures)) manquants.push(`${code}:HISTORIQUE_ABSENT`);
    const actualiseLe = dateIsoValide(situation.actualise_le);
    if (dateArrete && actualiseLe && actualiseLe < dateArrete) manquants.push(`${code}:CACHE_${actualiseLe}`);
  });
  if (!dateArrete) raisons.push('DATE_ARRETE_INVALIDE');
  if (!codesRequis.size) raisons.push('PERIMETRE_SANS_PREUVE');
  if (manquants.length) raisons.push('HISTORIQUE_CLIENT_INCOMPLET');
  const factures = raisons.length ? [] : facturesPreliminaires
    .map((facture) => ({ ...facture, source_dette: 'GRAND_LIVRE_INTERCLUB_D2' }));
  const audit = factures.reduce((resultat, facture) => ({
    factures: resultat.factures + Number(facture.montant_facture || 0),
    avoirs: resultat.avoirs + Number(facture.montant_avoirs || 0),
    paiements: resultat.paiements + Number(facture.montant_paye || 0),
    reste: resultat.reste + Number(facture.reste_a_payer || 0)
  }), { factures: 0, avoirs: 0, paiements: 0, reste: 0 });
  return { certifiable: raisons.length === 0, factures, raisons, manquants, audit };
}

export function facturesDetteDepuisSituationsInterclub(situations = [], documents = [], clubs = [], options = {}) {
  return certifierDetteDepuisSituationsInterclub(situations, documents, clubs, options).factures;
}

export function diagnostiquerPivotFinancier(documents = [], pivot = DATE_PIVOT_FINANCIERE) {
  const cycle = cycleFinancier(documents, { pivot });
  return {
    pivot: cycle.pivot,
    facturesIncluses: cycle.factures.length,
    facturesExclues: cycle.facturesExclues.length,
    reglementsInclus: cycle.paiements.length,
    reglementsExclus: cycle.paiementsExclus.length,
    reglementsOrphelins: cycle.reglementsOrphelins
  };
}

export function syntheseBoutiqueClub({ campagnes = [], commandes = [], types = [], attributions = [], bons = [], utilisationsCollectives = [], bonsDisponibles = true }, clubsFiltres = [], commercial = 'Tous', debut = '', fin = '') {
  const idsClubs = new Set(clubsFiltres.map((club) => String(club.id || club.identifiant)));
  const equipe = ['tous', "toute l'équipe"].includes(normaliser(commercial));
  const campagnesFiltrees = campagnes.filter((campagne) => {
    const date = String(campagne.ouverture_at || '').slice(0, 10);
    return idsClubs.has(String(campagne.club_id))
      && (equipe || normaliser(campagne.commercial_nom) === normaliser(commercial))
      && (!debut || (date && date >= debut))
      && (!fin || (date && date <= fin));
  });
  const ids = new Set(campagnesFiltrees.map((campagne) => String(campagne.id)));
  const idsEnCours = new Set(campagnesFiltrees.filter((campagne) => !campagneArchivee(campagne) && etatPilotageCampagne(campagne) !== 'FERMEES').map((campagne) => String(campagne.id)));
  const compteurs = compterCampagnes(campagnesFiltrees);
  const commandesValides = commandes.filter((commande) => idsEnCours.has(String(commande.campagne_id)) && commande.statut === 'VALIDEE');
  const typesFiltres = types.filter((type) => ids.has(String(type.campagne_id)));
  const attributionsFiltrees = attributions.filter((attribution) => ids.has(String(attribution.campagne_id)) && attribution.statut !== 'ANNULEE');
  const bonsFiltres = bons.filter((bon) => ids.has(String(bon.campagne_id)) && bon.statut !== 'ANNULE');
  const usagesCollectifs = utilisationsCollectives.filter((usage) => ids.has(String(usage.campagne_id)));
  const engagements = new Map();
  attributionsFiltrees.forEach((attribution) => engagements.set(attribution.type_bon_id, (engagements.get(attribution.type_bon_id) || 0) + 1));
  const engagementsCollectifs = new Map();
  usagesCollectifs.forEach((usage) => engagementsCollectifs.set(usage.type_bon_id, (engagementsCollectifs.get(usage.type_bon_id) || 0) + 1));
  const engages = (type) => type.mode_attribution === 'COLLECTIF' ? (engagementsCollectifs.get(type.id) || 0) : (engagements.get(type.id) || 0);
  const depasses = typesFiltres.filter((type) => engages(type) > Number(type.quota_autorise || 0)).length;
  return {
    total: compteurs.TOUTES,
    actives: compteurs.OUVERTES,
    aCloturer: compteurs.A_CLOTURER,
    fermees: compteurs.FERMEES,
    ca: commandesValides.reduce((total, commande) => total + Number(commande.total_ttc || 0), 0),
    bonsDansQuota: bonsDisponibles ? bonsFiltres.filter((bon) => { const type = typesFiltres.find((item) => item.id === bon.type_bon_id); return type && !campagneArchivee(campagnesFiltrees.find((campagne) => campagne.id === type.campagne_id)) && engages(type) <= Number(type.quota_autorise || 0); }).length : null,
    demandesSupplementaires: null,
    depassements: bonsDisponibles ? depasses : null,
    alertes: bonsDisponibles ? depasses : null,
    campagnes: campagnesFiltrees
  };
}
